declare global {
  interface Window {
    initMap?: () => void;
    gm_authFailure?: () => void;
  }
}

// https://developers.google.com/maps/documentation/javascript/libraries#libraries-for-dynamic-library-import
type DynamicLibraryName =
  | 'core'
  | 'maps'
  | 'places'
  | 'geocoding'
  | 'routes'
  | 'marker'
  | 'geometry'
  | 'elevation'
  | 'streetView'
  | 'journeySharing'
  | 'drawing'
  | 'visualization';

export declare interface LoaderOptions {
  version?: string;
  client?: string;
  channel?: string;
  language?: string;
  region?: string;
}

let currentlyLoadingMapsApi: Promise<typeof google> | undefined;
const loadedMapsLibraries = new Map<
  DynamicLibraryName,
  ReturnType<typeof google.maps.importLibrary>
>();

export class GoogleMapsLoader {
  constructor(
    private apiKey: string | null = null,
    private options: LoaderOptions = {}
  ) {
    if (!window) {
      throw new Error('google-maps is supported only in browser environment');
    }
  }

  public async loadMapsApi(): Promise<typeof google> {
    if (typeof window.google?.maps?.importLibrary === 'function') {
      return new Promise((resolve) => resolve(window.google));
    }

    if (!currentlyLoadingMapsApi) {
      currentlyLoadingMapsApi = new Promise<typeof google>(
        (resolve, reject) => {
          window.initMap = (): void => {
            resolve(window.google);
            currentlyLoadingMapsApi = undefined;
            delete window.initMap;
          };
          window.gm_authFailure = (): void => {
            reject(new Error('google-maps: authentication error'));
            delete window.gm_authFailure;
          };
          const script = document.createElement('script');
          script.src = this.createUrl();
          script.async = true;
          script.onerror = (error): void =>
            reject(new Error(typeof error === 'string' ? error : error.type));
          document.head.appendChild(script);
        }
      );
    }

    return currentlyLoadingMapsApi;
  }

  public loadLibrary(name: 'core'): Promise<google.maps.CoreLibrary>;
  public loadLibrary(name: 'maps'): Promise<google.maps.MapsLibrary>;
  public loadLibrary(name: 'places'): Promise<google.maps.PlacesLibrary>;
  public loadLibrary(name: 'geocoding'): Promise<google.maps.GeocodingLibrary>;
  public loadLibrary(name: 'routes'): Promise<google.maps.RoutesLibrary>;
  public loadLibrary(name: 'marker'): Promise<google.maps.MarkerLibrary>;
  public loadLibrary(name: 'geometry'): Promise<google.maps.GeometryLibrary>;
  public loadLibrary(name: 'elevation'): Promise<google.maps.ElevationLibrary>;
  public loadLibrary(
    name: 'streetView'
  ): Promise<google.maps.StreetViewLibrary>;
  public loadLibrary(
    name: 'journeySharing'
  ): Promise<google.maps.JourneySharingLibrary>;
  public loadLibrary(name: 'drawing'): Promise<google.maps.DrawingLibrary>;
  public loadLibrary(
    name: 'visualization'
  ): Promise<google.maps.VisualizationLibrary>;
  public async loadLibrary(
    libraryName: DynamicLibraryName
  ): ReturnType<typeof google.maps.importLibrary> {
    const existingLibrary = loadedMapsLibraries.get(libraryName);

    if (existingLibrary) {
      return existingLibrary;
    }

    const importProcess = this.loadMapsApi()
      .then((googleObj: typeof google) =>
        googleObj.maps
          .importLibrary(libraryName)
          .then((importedLibrary) => {
            loadedMapsLibraries.set(
              libraryName,
              Promise.resolve(importedLibrary)
            );
            return importedLibrary;
          })
          .catch((error) => {
            loadedMapsLibraries.delete(libraryName);
            throw error;
          })
      )
      .catch((error) => {
        throw error;
      });

    loadedMapsLibraries.set(libraryName, importProcess);

    return importProcess;
  }

  private createUrl(): string {
    const parameters: string[] = ['callback=initMap', 'loading=async'];
    if (this.apiKey) {
      parameters.push(`key=${this.apiKey}`);
    }
    if (this.options.language) {
      parameters.push(`language=${this.options.language}`);
    }
    if (this.options.version) {
      parameters.push(`v=${this.options.version}`);
    }
    if (this.options.client) {
      parameters.push(`client=${this.options.client}`);
    }
    if (this.options.channel) {
      parameters.push(`channel=${this.options.channel}`);
    }
    if (this.options.region) {
      parameters.push(`region=${this.options.region}`);
    }
    return `https://maps.googleapis.com/maps/api/js?${parameters.join('&')}`;
  }
}
